import { runIfFn } from '@mntn-dev/utilities'
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from 'react'

export type UseControllableStateProps<T> = {
  value?: T
  defaultValue?: T
  onChange?: (value: T) => void
}

export function useControllableState<T>({
  defaultValue,
  onChange,
  value: valueProp,
}: UseControllableStateProps<T>) {
  const [valueState, setValueState] = useState<T | null | undefined>(
    defaultValue
  )

  const isControlled = valueProp !== undefined
  const value = isControlled ? valueProp : valueState

  const updateValue = useCallback(
    (next: SetStateAction<T>) => {
      const nextValue = runIfFn(next, value)

      if (value === nextValue) {
        return
      }

      if (!isControlled) {
        setValueState(nextValue)
      }

      onChange?.(nextValue)
    },
    [isControlled, onChange, value]
  )

  return [value, updateValue] as [T, Dispatch<SetStateAction<T>>]
}
